import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import orderService from './orderService'

const initialState = {
  // data for search
  page: 1,
  limit: 100,
  sort_key: 'created_at',
  sort_order: 'desc',
  order_key: '',
  user_id: '',
  status: -1,
  printing_status: -1,
  company_id: -1,
  since_last_logout: 0,
  dataLoading: false,
  // end data for search

  // order_status: null,
  order: null,
  order_detail: null,
  order_detail_contact: null,

  order_media: null,

  destinations: null,
  destinations_list: null,
  selected_destinations: null,

  orderError: null,
  orderLoading: false,
  order_destination_loading: false,
}

// @desc Get Search Order
export const getSearchOrder = createAsyncThunk(
  'order_search/get',
  async ({ data, token }, thunkAPI) => {
    try {
      return await orderService.getSearchOrder(data, token)
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data)
    }
  },
)
// @desc Get Item CSV
export const getItemCsv = createAsyncThunk(
  'order_search/get',
  async ({ data, token }, thunkAPI) => {
    try {
      return await orderService.getItemCsv(data, token)
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data)
    }
  },
)

// @desc Get List CSV
export const getListCsv = createAsyncThunk(
  'order_search/get',
  async ({ data, token }, thunkAPI) => {
    try {
      return await orderService.getListCsv(data, token)
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data)
    }
  },
)

// @desc Get Order Detail
export const getOrderDetail = createAsyncThunk(
  'order_detail/get',
  async ({ data, token }, thunkAPI) => {
    try {
      return await orderService.getOrderDetail(data, token)
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data)
    }
  },
)

// @desc Get Order Detail Contact
export const getOrderDetailContact = createAsyncThunk(
  'order_detail_contact/get',
  async ({ data, token }, thunkAPI) => {
    try {
      return await orderService.getOrderDetailContact(data, token)
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data)
    }
  },
)

// @desc Get Order Media
// Legacy /download_media path, kept for reference. No longer called.
export const getOrderMedia = createAsyncThunk(
  'order_media/get',
  async ({ data, token }, thunkAPI) => {
    try {
      return await orderService.getOrderMedia(data, token)
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data)
    }
  },
)
// @desc Download Order Media via GCS signed URL
export const downloadMediaViaSignedUrl = createAsyncThunk(
  'order_media_url/get',
  async ({ data, token }, thunkAPI) => {
    try {
      const { res, newToken } = await orderService.getOrderMediaUrl(data, token)
      if (res?.data?.result !== 'success' || !res.data.value?.url) {
        return thunkAPI.rejectWithValue(res?.data)
      }
      // Hidden iframe: a download leaves the page untouched, and an error response
      // (e.g. expired signature) renders invisibly instead of replacing the SPA.
      const iframe = document.createElement('iframe')
      iframe.hidden = true
      iframe.src = res.data.value.url
      document.body.appendChild(iframe)
      setTimeout(() => iframe.remove(), 60000)
      return { newToken }
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data)
    }
  },
)
// @desc Get Order Media
export const getMediaName = createAsyncThunk(
  '/get',
  async ({ data, token }, thunkAPI) => {
    try {
      return await orderService.getMediaName(data, token)
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data)
    }
  },
)

// @desc Update Order Status
export const updateOrderStatus = createAsyncThunk(
  'order_status/update',
  async ({ dataStatusChange, token }, thunkAPI) => {
    try {
      return await orderService.updateOrderStatus(dataStatusChange, token)
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data)
    }
  },
)

// @desc Force Cancel
export const forceCancelOrder = createAsyncThunk(
  'order/forced_cancel',
  async ({ dataStatusChange, token }, thunkAPI) => {
    try {
      return await orderService.forceCancelOrder(dataStatusChange, token)
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data)
    }
  },
)

// @desc Get Destinations Area
export const getDestinationsArea = createAsyncThunk(
  'destinations/get',
  async (token, thunkAPI) => {
    try {
      return await orderService.getDestinationsArea(token)
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data)
    }
  },
)

// @desc Update Destinations
export const updateOrderDestination = createAsyncThunk(
  'order_destination/update',
  async ({ dataUpdateOrderDestination, token }, thunkAPI) => {
    try {
      return await orderService.updateOrderDestination(
        dataUpdateOrderDestination,
        token,
      )
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data)
    }
  },
)

// @desc Update Distribution Quantity
export const updateDistributionQuantity = createAsyncThunk(
  'distribution_quantity/update',
  async ({ dataQuantityChange, token }, thunkAPI) => {
    try {
      return await orderService.updateDistributionQuantity(
        dataQuantityChange,
        token,
      )
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data)
    }
  },
)

// @desc Delete Distribution Destination
export const deleteDistributionDestination = createAsyncThunk(
  'distribution_destination/delete',
  async ({ dataDelete, token }, thunkAPI) => {
    try {
      return await orderService.deleteDistributionDestination(dataDelete, token)
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data)
    }
  },
)

// @desc Confirm Distribution Destination
export const confirmDistributionDestination = createAsyncThunk(
  'distribution_destination/confirm',
  async ({ data, token }, thunkAPI) => {
    try {
      return await orderService.confirmDistributionDestination(data, token)
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data)
    }
  },
)

// =========================== Sync ===========================
// @desc Update Sync Data
export const updateSyncData = createAsyncThunk(
  'sync_data/update',
  async ({ dataSync, token }, thunkAPI) => {
    try {
      return await orderService.updateSyncData(dataSync, token)
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data)
    }
  },
)

// @desc Update Sync Data
export const updateSyncDataOrikomi = createAsyncThunk(
  'sync_data_orikomi/update',
  async ({ dataSync, token }, thunkAPI) => {
    try {
      return await orderService.updateSyncDataOrikomi(dataSync, token)
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data)
    }
  },
)

export const orderSlice = createSlice({
  name: 'order',
  initialState,
  reducers: {
    setDataLoading: (state) => {
      state.dataLoading = !state.dataLoading
    },
    resetOrder: (state) => {
      state.order_status = null
      state.order = null
      state.limit = 100
      state.page = 1
      state.order_detail = null
      state.order_detail_contact = null
      state.distribution_status = null
      state.order_media = null
      state.destinations = null
      state.destinations_list = null
      state.orderError = null
    },
    // Search
    setOrderKeySearch: (state, action) => {
      state.order_key = action.payload
    },
    setUserIdSearch: (state, action) => {
      state.user_id = action.payload
    },
    setStatusSearch: (state, action) => {
      state.status = action.payload
    },
    setPrintingStatusSearch: (state, action) => {
      state.printing_status = action.payload
    },
    setSinceLasLogout: (state, action) => {
      state.since_last_logout = action.payload
    },
    // Sort
    setSort: (state, action) => {
      state.sort_key = action.payload
    },
    setLimit: (state, action) => {
      state.limit = action.payload
    },
    setPage: (state, action) => {
      state.page = action.payload
    },
    // Reset Search/Sort condition
    resetSearchSort: (state) => {
      state.order_key = ''
      state.user_id = ''
      state.status = -1
      state.printing_status = -1
      state.page = 1
      state.limit = 100
      state.sort_key = 'created_at'
      state.sort_order = 'desc'
      state.company_id = -1
      state.since_last_logout = 0
    },
    resetSort: (state) => {
      state.sort_key = 'created_at'
    },
    setSortOrder: (state, action) => {
      state.sort_order = action.payload
    },
    setDestinationList: {
      reducer: (state, action) => {
        state.destinations_list = []
        state.destinations_list.push(action.payload)
      },
      prepare: (value) => {
        return {
          payload: value.map((val) => ({
            ...val,
            checked: false,
          })),
        }
      },
    },
  },
  extraReducers: (builder) => {
    builder
      // GET Serach Order
      .addCase(getSearchOrder.pending, (state) => {
        state.orderLoading = true
      })
      .addCase(getSearchOrder.fulfilled, (state, action) => {
        state.order = action.payload.res.data
        state.orderLoading = false
      })
      .addCase(getSearchOrder.rejected, (state, action) => {
        state.orderError = action.payload
        state.orderLoading = false
      })
      // GET Detail Order Detail
      .addCase(getOrderDetail.pending, (state) => {
        state.order_detail = null
        state.orderLoading = true
      })
      .addCase(getOrderDetail.fulfilled, (state, action) => {
        state.order_detail = action.payload.res.data
        state.orderLoading = false
      })
      .addCase(getOrderDetail.rejected, (state, action) => {
        state.orderError = action.payload
        state.orderLoading = false
      })
      // GET Detail Order Detail Contact
      .addCase(getOrderDetailContact.pending, (state) => {
        state.order_detail_contact = null
        state.orderLoading = true
      })
      .addCase(getOrderDetailContact.fulfilled, (state, action) => {
        state.order_detail_contact = action.payload.res.data
        state.orderLoading = false
      })
      .addCase(getOrderDetailContact.rejected, (state, action) => {
        state.orderError = action.payload
        state.orderLoading = false
      })
      // GET Order Media
      .addCase(getOrderMedia.pending, (state) => {
        state.order_media = null
        // state.orderLoading = true
      })
      .addCase(getOrderMedia.fulfilled, (state, action) => {
        state.order_media = action.payload.data
        state.orderLoading = false
      })
      .addCase(getOrderMedia.rejected, (state, action) => {
        state.orderError = action.payload
        state.orderLoading = false
      })
      // GET Order Media Signed URL
      .addCase(downloadMediaViaSignedUrl.rejected, (state, action) => {
        state.orderError = action.payload
      })
      // UPDATE Order Status
      .addCase(updateOrderStatus.pending, (state) => {
        state.orderLoading = true
      })
      .addCase(updateOrderStatus.fulfilled, (state, action) => {
        state.orderLoading = false
      })
      .addCase(updateOrderStatus.rejected, (state, action) => {
        state.orderError = action.payload
        state.orderLoading = false
      })
      // FORCED CANCEL Order
      .addCase(forceCancelOrder.pending, (state) => {
        state.orderLoading = true
      })
      .addCase(forceCancelOrder.fulfilled, (state, action) => {
        state.orderLoading = false
      })
      .addCase(forceCancelOrder.rejected, (state, action) => {
        state.orderError = action.payload
        state.orderLoading = false
      })
      // GET Destinations Area
      .addCase(getDestinationsArea.pending, (state) => {
        state.destinations = null
        state.order_destination_loading = true
      })
      .addCase(getDestinationsArea.fulfilled, (state, action) => {
        state.destinations = action.payload.res.data
        state.order_destination_loading = false
      })
      .addCase(getDestinationsArea.rejected, (state, action) => {
        state.orderError = action.payload
        state.order_destination_loading = false
      })
      // UPDATE Order Destinations Area
      .addCase(updateOrderDestination.pending, (state) => {
        state.order_destination_loading = true
      })
      .addCase(updateOrderDestination.fulfilled, (state) => {
        state.order_destination_loading = false
      })
      .addCase(updateOrderDestination.rejected, (state, action) => {
        state.orderError = action.payload
        state.order_destination_loading = false
      })
      // UPDATE Order Distrobution Quantity
      .addCase(updateDistributionQuantity.pending, (state) => {
        state.order_destination_loading = true
      })
      .addCase(updateDistributionQuantity.fulfilled, (state) => {
        state.order_destination_loading = false
      })
      .addCase(updateDistributionQuantity.rejected, (state, action) => {
        state.orderError = action.payload
        state.order_destination_loading = false
      })
      // DELETE Order Distrobution Destination
      .addCase(deleteDistributionDestination.pending, (state) => {
        state.order_destination_loading = true
      })
      .addCase(deleteDistributionDestination.fulfilled, (state) => {
        state.order_destination_loading = false
      })
      .addCase(deleteDistributionDestination.rejected, (state, action) => {
        state.orderError = action.payload
        state.order_destination_loading = false
      })
      // Confirm Distribution Destination
      .addCase(confirmDistributionDestination.pending, (state) => {
        state.orderLoading = true
      })
      .addCase(confirmDistributionDestination.fulfilled, (state) => {
        state.orderLoading = false
      })
      .addCase(confirmDistributionDestination.rejected, (state, action) => {
        state.orderError = action.payload
        state.orderLoading = false
      })
      // =========================== Sync ===========================
      // UPDATE Sync Data
      .addCase(updateSyncData.pending, (state) => {
        state.orderLoading = true
      })
      .addCase(updateSyncData.fulfilled, (state) => {
        state.orderLoading = false
      })
      .addCase(updateSyncData.rejected, (state, action) => {
        state.orderError = action.payload
        state.orderLoading = false
      })
      // UPDATE Sync Data Orikomi
      .addCase(updateSyncDataOrikomi.pending, (state) => {
        state.orderLoading = true
      })
      .addCase(updateSyncDataOrikomi.fulfilled, (state) => {
        state.orderLoading = false
      })
      .addCase(updateSyncDataOrikomi.rejected, (state, action) => {
        state.orderError = action.payload
        state.orderLoading = false
      })
  },
})

export const {
  setDataLoading,
  resetOrder,
  setSort,
  resetSort,
  setSortOrder,
  setDestinationList,
  // Limit and page
  setLimit,
  setPage,
  // Serach
  setStatusSearch,
  setPrintingStatusSearch,
  setOrderKeySearch,
  setUserIdSearch,
  resetSearchSort,
  setSinceLasLogout,
} = orderSlice.actions

export default orderSlice.reducer
